import React, { useState, useEffect, useRef } from 'react';
import { prioritySkills, skillRatings } from '../../api';
import { BRANCHES, getSkillFullName } from '../SkillTree/data';
import TapDatePicker from '../DatePicker/TapDatePicker';
import './CoachPriorityAssignModal.css';

const CoachPriorityAssignModal = ({ isOpen, onClose, student, skillTree, onSave, asSection = false }) => {
  const [selectedSkills, setSelectedSkills] = useState({});
  const [deadlines, setDeadlines] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [playerRatings, setPlayerRatings] = useState({});
  const [assignedSkillParents, setAssignedSkillParents] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const lastLoadedStudentIdRef = useRef(null);

  // Load skills when modal opens or student changes
  useEffect(() => {
    if (isOpen && student && student._id) {
      // Only load if this is a different student
      if (lastLoadedStudentIdRef.current !== student._id) {

        // Reset form
        setSelectedSkills({});
        setDeadlines({});
        setError('');
        setSuccess(false);
        setExpandedNodes({});

        // Fetch both existing priorities and player's acquired skills
        loadPlayerData(student._id);
        lastLoadedStudentIdRef.current = student._id;
      }
    }
  }, [isOpen, student]);

  // Refs and hooks must be declared before any early returns
  const skillTreeContainerRef = React.useRef(null);

  // Scroll to last selected skill when nodes are expanded (when editing existing priorities)
  useEffect(() => {
    if (Object.keys(selectedSkills).length > 0 && skillTreeContainerRef.current) {
      // Small delay to allow DOM to render expanded nodes
      const timer = setTimeout(() => {
        const allSelectedSkills = skillTreeContainerRef.current?.querySelectorAll('[data-selected="true"]');
        const lastSelectedSkill = allSelectedSkills?.[allSelectedSkills.length - 1];
        if (lastSelectedSkill) {
          lastSelectedSkill.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedSkills]);

  const loadPlayerData = async (playerId) => {
    try {
      // Load player's skill ratings (what they've acquired)
      const ratingsResponse = await skillRatings.get(playerId);
      if (ratingsResponse.data && ratingsResponse.data.ratings) {
        setPlayerRatings(ratingsResponse.data.ratings);
      }

      // Fetch existing priorities for this student
      await fetchExistingPriorities(playerId);
    } catch (err) {
      console.error('❌ Error loading player data:', err);
    }
  };

  // Find all parent nodes for a skill (to auto-expand the tree)
  const findParentNodes = (skillId, branches = BRANCHES) => {
    const parents = new Set();

    const traverse = (node, parentIds = []) => {
      if (node.id === skillId) {
        parentIds.forEach(id => parents.add(id));
        return true;
      }

      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          if (traverse(child, [...parentIds, node.id])) {
            return true;
          }
        }
      }
      return false;
    };

    branches.forEach(branch => traverse(branch));
    return parents;
  };

  // Does this node (or any of its descendants) match the search query? Used
  // both to decide whether to render a node at all while searching, and to
  // force-expand every branch that contains a match so results are visible
  // without the coach having to manually drill down a 5-6 level deep tree.
  const nodeMatchesSearch = (node, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    if (node.title && node.title.toLowerCase().includes(q)) return true;
    return !!(node.children && node.children.some(child => nodeMatchesSearch(child, query)));
  };

  // All category (non-leaf) node ids, for Expand All / Collapse All.
  const getAllCategoryIds = (branches = BRANCHES) => {
    const ids = [];
    const traverse = (node) => {
      if (node.children && node.children.length > 0) {
        ids.push(node.id);
        node.children.forEach(traverse);
      }
    };
    branches.forEach(traverse);
    return ids;
  };

  const handleExpandAll = () => {
    const expanded = {};
    getAllCategoryIds().forEach(id => { expanded[id] = true; });
    setExpandedNodes(expanded);
  };

  const handleCollapseAll = () => {
    setExpandedNodes({});
  };

  // Fetch existing priority skills for the selected student
  const fetchExistingPriorities = async (playerId) => {
    try {
      const response = await prioritySkills.getPlayerPriorities(playerId);
      if (response.data && response.data.prioritySkill) {
        const skills = response.data.prioritySkill;

        // Rebuild selectedSkills object from existing priorities
        const skillsObj = {};
        const deadlinesObj = {};
        const nodesToExpand = new Set();

        // Get target measures from the Map (stored as object in response)
        const targetMeasures = skills.targetMeasures || {};

        for (let i = 0; i < skills.skillIds.length; i++) {
          const skillId = skills.skillIds[i];
          const measure = targetMeasures[skillId] || 3;
          skillsObj[skillId] = measure;

          // Find parent nodes and mark them for expansion
          const parents = findParentNodes(skillId);
          parents.forEach(id => nodesToExpand.add(id));

          // Check if deadline exists
          if (skills.deadlineTargets && skills.deadlineTargets[skillId]) {
            deadlinesObj[skillId] = new Date(skills.deadlineTargets[skillId])
              .toISOString()
              .split('T')[0];
          }
        }

        console.log('📂 Expanding parent nodes:', Array.from(nodesToExpand));
        setSelectedSkills(skillsObj);
        setDeadlines(deadlinesObj);

        // Auto-expand nodes that have assigned priorities
        const expandedObj = {};
        nodesToExpand.forEach(id => {
          expandedObj[id] = true;
        });
        setExpandedNodes(expandedObj);
      }
    } catch (err) {
      console.warn('⚠️ Failed to fetch existing priorities:', err);
      // Don't show error, just start fresh
    }
  };

  if (!isOpen || !student) {
    return null;
  }

  // Toggle tree node expansion
  const toggleNodeExpand = (nodeId) => {
    setExpandedNodes({
      ...expandedNodes,
      [nodeId]: !expandedNodes[nodeId]
    });
  };

  // Handle skill selection with target measure (1, 2, or 3)
  const handleSkillToggle = (skillId, maxRating) => {
    const isSelected = selectedSkills[skillId];

    if (isSelected) {
      // Deselect
      const newSelected = { ...selectedSkills };
      delete newSelected[skillId];
      setSelectedSkills(newSelected);

      const newDeadlines = { ...deadlines };
      delete newDeadlines[skillId];
      setDeadlines(newDeadlines);
    } else {
      // Select (if under limit)
      const currentCount = Object.keys(selectedSkills).length;
      if (currentCount < 10) {
        // Default to max rating for this skill
        setSelectedSkills({
          ...selectedSkills,
          [skillId]: maxRating || 3
        });
      }
    }
  };

  const handleMeasureChange = (skillId, measure) => {
    setSelectedSkills({
      ...selectedSkills,
      [skillId]: parseInt(measure, 10)
    });
  };

  const handleDeadlineChange = (skillId, date) => {
    setDeadlines({
      ...deadlines,
      [skillId]: date
    });
  };

  const handleAssign = async () => {
    // Validate selection
    const selectedCount = Object.keys(selectedSkills).length;
    if (selectedCount < 3) {
      setError('Please select at least 3 skills');
      return;
    }

    if (selectedCount > 10) {
      setError('Maximum 10 skills allowed');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Prepare skills array with measures. skillName is the full breadcrumb
      // (e.g. "Defense › Direct › Forehand › Flat › Depth") - only the
      // frontend has the tree structure to resolve it, and the backend has
      // no way to produce a readable name from the id alone.
      const skillsWithMeasures = Object.entries(selectedSkills).map(([skillId, measure]) => ({
        skillId,
        targetMeasure: measure,
        skillName: getSkillFullName(skillId)
      }));

      // Assign priorities with measures
      const assignResponse = await prioritySkills.assignPriorities(student._id, skillsWithMeasures);

      // Verify by fetching immediately
      const verifyResponse = await prioritySkills.getPlayerPriorities(student._id);

      // Set deadlines if any were provided
      if (Object.keys(deadlines).length > 0) {
        const deadlineDates = {};
        Object.entries(deadlines).forEach(([skillId, date]) => {
          if (date) {
            deadlineDates[skillId] = date;
          }
        });

        if (Object.keys(deadlineDates).length > 0) {
          await prioritySkills.updatePriorities(student._id, {
            deadlineDates
          });
        }
      }

      setSuccess(true);
      if (onSave) onSave();

      // Close after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('❌ [handleAssign] Error assigning priorities:', err);
      setError(err.response?.data?.message || 'Failed to assign priorities');
    } finally {
      setLoading(false);
    }
  };

  // Recursive component to render skill tree
  const SkillTreeNode = ({ node, depth = 0 }) => {
    const isLeaf = !node.children || node.children.length === 0;
    const isSelected = selectedSkills[node.id];
    // While searching, force every branch open so matches are visible
    // without manually expanding each level - otherwise fall back to
    // whatever the coach has expanded/collapsed by hand.
    const isExpanded = searchQuery ? true : expandedNodes[node.id];
    const selectedCount = Object.keys(selectedSkills).length;

    // Check player's acquired rating for this skill
    const playerRating = playerRatings[node.id] || 0;
    const maxRating = node.maxRating || 3;
    const fullyAcquired = playerRating >= maxRating;
    const availableLevels = [];

    // Build list of available levels (those not yet acquired)
    for (let level = 1; level <= maxRating; level++) {
      if (level > playerRating) {
        availableLevels.push(level);
      }
    }

    const hasAvailableLevels = availableLevels.length > 0;
    const acquiredText = playerRating > 0 ? ` (Acquired: Level ${playerRating})` : '';

    // Create ref for scroll-to-view when skill is selected
    const skillRef = React.useRef(null);

    // Scroll selected skills into view
    React.useEffect(() => {
      if (isSelected && skillRef.current) {
        skillRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, [isSelected]);

    // HIDE fully acquired leaf skills (don't render them at all)
    if (isLeaf && fullyAcquired) {
      return null;
    }

    // Hide this node entirely if it (and none of its descendants) match
    // the current search. Placed after every hook above (Rules of Hooks -
    // an early return before a hook would change how many hooks this
    // component instance calls between renders whenever the search text
    // changes what matches).
    if (searchQuery && !nodeMatchesSearch(node, searchQuery)) {
      return null;
    }

    // Each node in this recursive tree wraps its own children, so
    // marginLeft on every level NESTS - the browser adds this node's own
    // margin on top of every ancestor's margin, it doesn't replace them.
    // A previous fix tried to cap this with `Math.min(depth, 5) * 14`, but
    // that computes an already-capped ABSOLUTE value and then re-applies
    // it as a RELATIVE margin at every level, so it still compounds: at
    // depth 5 the real on-screen offset was 14+28+42+56+70 = 210px, not
    // the intended 70px cap (confirmed by measuring actual rendered x
    // positions on a 373px viewport - FOREHAND and Depth were clipped off
    // the right edge). The fix is a flat per-level step that simply stops
    // contributing once the cap depth is reached, so the compounding
    // converges to the intended total instead of restarting it at every
    // level: 12px x 5 levels = 60px max, however deep the tree actually
    // goes (this tree reaches depth 6).
    const indent = depth > 0 && depth <= 5 ? 12 : 0;

    return (
      <div key={node.id} className="skill-tree-node" style={{ marginLeft: `${indent}px` }} ref={isLeaf && isSelected ? skillRef : null} data-selected={isLeaf && isSelected ? 'true' : undefined}>
        <div
          className={`node-header ${isLeaf && isSelected ? 'node-selected' : ''}`}
          onClick={() => {
            // For category nodes, expand/collapse on entire header click
            if (!isLeaf) {
              toggleNodeExpand(node.id);
            }
          }}
          style={!isLeaf ? { cursor: 'pointer' } : {}}
        >
          {/* Expand/Collapse Toggle */}
          {!isLeaf && (
            <button
              className={`expand-toggle ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation(); // Prevent double-triggering
                toggleNodeExpand(node.id);
              }}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              ▶
            </button>
          )}
          {isLeaf && <span className="expand-toggle empty" />}

          {/* Checkbox for leaf nodes only */}
          {isLeaf ? (
            <label className={`skill-checkbox-label ${isSelected ? 'selected' : ''} ${!hasAvailableLevels ? 'disabled' : ''}`} title={fullyAcquired ? 'All levels of this skill are already acquired' : ''}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleSkillToggle(node.id, maxRating)}
                disabled={!hasAvailableLevels || (fullyAcquired && !isSelected)}
                title={fullyAcquired ? 'All levels acquired - cannot be assigned' : !hasAvailableLevels ? 'All remaining levels must be acquired first' : ''}
              />
              <span className="checkbox-custom" />
              <span className="skill-label">
                {node.title}
                {playerRating > 0 && <span style={{ fontSize: '12px', color: fullyAcquired ? '#e74c3c' : '#999', marginLeft: '6px', fontWeight: fullyAcquired ? '600' : 'normal' }}>{acquiredText}{fullyAcquired ? ' ✓ Complete' : ''}</span>}
              </span>
            </label>
          ) : (
            <span className="skill-category-label" style={{ pointerEvents: 'none' }}>{node.title}</span>
          )}
        </div>

        {/* Measure & Deadline Controls for selected leaf skills */}
        {isLeaf && isSelected && (
          <div className="skill-controls">
            <div className="measure-selector">
              <label htmlFor={`measure-${node.id}`}>Target Level:</label>
              <select
                id={`measure-${node.id}`}
                value={selectedSkills[node.id] || (availableLevels.length > 0 ? availableLevels[availableLevels.length - 1] : maxRating)}
                onChange={(e) => handleMeasureChange(node.id, e.target.value)}
                className="measure-select"
              >
                {availableLevels.map(level => (
                  <option key={level} value={level}>
                    Level {level}
                  </option>
                ))}
              </select>
              {playerRating > 0 && (
                <div className="skill-levels-hint" style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  Levels 1-{playerRating} already acquired
                </div>
              )}
            </div>

            <div className="deadline-selector">
              <label htmlFor={`deadline-${node.id}`}>Deadline (optional):</label>
              <TapDatePicker
                id={`deadline-${node.id}`}
                value={deadlines[node.id] || ''}
                onChange={(date) => handleDeadlineChange(node.id, date)}
                placeholder="Select date"
              />
            </div>
          </div>
        )}

        {/* Render children if expanded */}
        {!isLeaf && isExpanded && node.children && (
          <div className="skill-tree-children">
            {node.children.map(child => (
              <SkillTreeNode key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const selectedCount = Object.keys(selectedSkills).length;

  // Content structure (shared between modal and section modes)
  const content = (
    <>
      {/* Sticky top block - header, count, search/expand controls, and any
          error/success messages all move together and stay visible while
          the skill list below scrolls, so validation feedback and the
          selection count are never scrolled out of sight. */}
      <div className="priority-sticky-top">
        {/* Header */}
        <div className="modal-header">
          <h2>⭐ Assign Priority Skills to {student.name}</h2>
          {asSection && <button className="modal-close" onClick={onClose}>✕</button>}
        </div>

        {/* Info */}
        <div className="modal-info">
          <p>Select 3-10 specific skill leaves and set your target level (1-3) for each</p>
          <div className="selection-count">
            Selected: {selectedCount}/10 skills
          </div>
        </div>

        {/* Search + Expand/Collapse - the tree goes 5-6 levels deep, so
            finding one specific skill by hand-expanding branches is slow;
            search jumps straight to matches and auto-expands their branch. */}
        <div className="skill-tree-toolbar">
          <div className="skill-search">
            <span className="skill-search-icon">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search skills..."
              className="skill-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="skill-search-clear"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <div className="skill-tree-toolbar-actions">
            <button type="button" className="tree-action-btn" onClick={handleExpandAll}>
              Expand All
            </button>
            <button type="button" className="tree-action-btn" onClick={handleCollapseAll}>
              Collapse All
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="error-message">
            <span className="error-icon">❌</span>
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="success-message">
            <span className="success-icon">✅</span>
            <p>Priority skills assigned successfully!</p>
          </div>
        )}
      </div>

        {/* Skill Selection */}
        <div className="skill-selection-section">
          <div className="skill-tree-container" ref={skillTreeContainerRef}>
            {BRANCHES.length === 0 ? (
              <div className="no-skills-message">
                No skills available in the skill tree
              </div>
            ) : searchQuery && !BRANCHES.some(branch => nodeMatchesSearch(branch, searchQuery)) ? (
              <div className="no-skills-message">
                No skills match "{searchQuery}"
              </div>
            ) : (
              BRANCHES.map(branch => (
                <SkillTreeNode key={branch.id} node={branch} depth={0} />
              ))
            )}
          </div>
        </div>

      {/* Sticky bottom block - validation warning + action buttons stay
          reachable regardless of how far down the skill list is scrolled,
          instead of requiring a scroll to the very bottom of a long tree
          (or, in section mode, the whole page) to find the Assign button. */}
      <div className="priority-sticky-bottom">
        {/* Selection Validation */}
        {selectedCount < 3 && selectedCount > 0 && (
          <div className="warning-message">
            <span className="warning-icon">⚠️</span>
            <p>Select at least 3 skills ({3 - selectedCount} more needed)</p>
          </div>
        )}

        {/* Footer Buttons */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {asSection ? 'Back' : 'Cancel'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAssign}
            disabled={loading || selectedCount < 3 || success}
          >
            {loading ? 'Assigning...' : `Assign ${selectedCount} Skills`}
          </button>
        </div>
      </div>
    </>
  );

  // Render as modal overlay or section based on asSection prop
  if (asSection) {
    return (
      <div className="st-tab-content st-tab-content--priority-skills">
        {content}
      </div>
    );
  }

  return (
    <div className="modal-overlay-fullscreen" onClick={onClose}>
      <div className="modal-content assign-modal-fullscreen" onClick={e => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
};

export default CoachPriorityAssignModal;
